/**
 * Patron Points — PLAYER PROFILE. Read only.
 *
 * Every section is a list of rows the player's own play already wrote. There
 * is no editable field on this screen and no balance to change: the points
 * figure is the sum of the history shown underneath it.
 */
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Calendar, Coins, Gift, Star, Users, Wifi } from 'lucide-react';
import api from '../../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { EmailChip, PrizeChip, VisitChip } from './PatronChips';

const num = (n) => Number(n || 0).toLocaleString();
const when = (iso) => (iso ? new Date(iso).toLocaleString(undefined,
    { year: 'numeric', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '—');
const day = (iso) => (iso ? new Date(iso).toLocaleDateString(undefined,
    { year: 'numeric', month: 'short', day: 'numeric' }) : '—');

const Section = ({ icon: Icon, title, rows, children, testid, t }) => (
    <Card data-testid={testid}>
        <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
                <Icon size={15} className="text-emerald-500" />{title}
                <span className="text-xs font-normal text-slate-500">{rows}</span>
            </CardTitle>
        </CardHeader>
        <CardContent className="space-y-1.5">
            {rows === 0 ? (
                <p className="text-sm text-slate-500">{t('patronCommand.profile.nothing')}</p>
            ) : children}
        </CardContent>
    </Card>
);

const Row = ({ left, right, note, testid }) => (
    <div className="flex items-start justify-between gap-3 border-b border-slate-100 dark:border-slate-800 pb-1.5 last:border-0"
         data-testid={testid}>
        <span className="text-sm min-w-0">
            {left}
            {note ? <span className="block text-xs text-slate-500">{note}</span> : null}
        </span>
        <span className="text-xs text-slate-500 whitespace-nowrap">{right}</span>
    </div>
);

export const PatronProfile = ({ orgId, fanId, onBack }) => {
    const { t } = useTranslation();
    const [p, setP] = useState(null);

    useEffect(() => {
        (async () => {
            const res = await api.get(`/patron/insights/player/${fanId}`,
                { params: { organization_id: orgId } });
            setP(res.data);
        })();
    }, [orgId, fanId]);

    if (!p) return <p className="text-sm text-slate-500" data-testid="patron-profile-loading">…</p>;

    return (
        <div className="space-y-4" data-testid="patron-profile">
            <Button variant="ghost" size="sm" onClick={onBack} data-testid="patron-profile-back">
                <ArrowLeft size={14} className="mr-1" />{t('patronCommand.profile.back')}
            </Button>

            <Card>
                <CardContent className="pt-5 flex flex-wrap items-center gap-x-6 gap-y-3">
                    <div className="grow min-w-0">
                        <p className="text-lg font-bold" data-testid="patron-profile-name">
                            {p.screen_name || p.email}
                        </p>
                        <p className="text-xs text-slate-500" data-testid="patron-profile-email">{p.email}</p>
                    </div>
                    <div>
                        <p className="text-xs uppercase tracking-wider text-slate-500">
                            {t('patronCommand.profile.points')}
                        </p>
                        <p className="text-2xl font-bold tabular-nums" data-testid="patron-profile-points">
                            {num(p.points)}
                        </p>
                    </div>
                    <div>
                        <p className="text-xs uppercase tracking-wider text-slate-500">
                            {t('patronCommand.profile.joined')}
                        </p>
                        <p className="text-sm font-semibold" data-testid="patron-profile-joined">{day(p.joined)}</p>
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                        <EmailChip status={p.email_status} t={t} testId="patron-profile-email-chip" />
                        <VisitChip recency={p.visit_recency} t={t} testId="patron-profile-visit-chip" />
                        {p.language ? (
                            <span className="text-[10px] uppercase text-slate-500">{p.language}</span>
                        ) : null}
                    </div>
                </CardContent>
            </Card>

            <p className="text-xs text-slate-500" data-testid="patron-profile-readonly">
                {t('patronCommand.profile.readOnly')}
            </p>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <Section icon={Calendar} t={t} testid="patron-profile-checkins"
                         title={t('patronCommand.profile.checkIns')} rows={p.check_ins.length}>
                    {p.check_ins.map((c, i) => (
                        <Row key={i} testid={`patron-checkin-${i}`}
                             left={c.label || c.night} right={when(c.at)} />
                    ))}
                </Section>

                <Section icon={Coins} t={t} testid="patron-profile-ledger"
                         title={t('patronCommand.profile.ledger')} rows={p.ledger.length}>
                    {p.ledger.map((r, i) => (
                        <Row key={i} testid={`patron-ledger-row-${i}`}
                             left={`${r.points > 0 ? '+' : ''}${num(r.points)} · ${t(`patronCommand.kinds.${r.kind}`, r.kind)}`}
                             note={[r.note, r.label, r.voucher_code].filter(Boolean).join(' · ')}
                             right={when(r.at)} />
                    ))}
                </Section>

                <Section icon={Star} t={t} testid="patron-profile-picks"
                         title={t('patronCommand.profile.picks')} rows={p.picks.length}>
                    <p className="text-xs text-slate-500 pb-1" data-testid="patron-profile-saved-star">
                        {p.saved_star?.player_name
                            ? t('patronCommand.profile.savedStar', { name: p.saved_star.player_name })
                            : t('patronCommand.profile.noSavedStar')}
                    </p>
                    {p.picks.map((s, i) => (
                        <Row key={i} testid={`patron-pick-${i}`} left={s.player_name}
                             note={s.label} right={when(s.at)} />
                    ))}
                </Section>

                <Section icon={Users} t={t} testid="patron-profile-tables"
                         title={t('patronCommand.profile.tables')} rows={p.tables.length}>
                    {p.tables.map((tb, i) => (
                        <Row key={i} testid={`patron-table-${i}`}
                             left={tb.label || tb.night} note={tb.card_id} right={when(tb.at)} />
                    ))}
                </Section>

                <Section icon={Gift} t={t} testid="patron-profile-prizes"
                         title={t('patronCommand.profile.prizes')} rows={p.prizes.length}>
                    {p.prizes.map((v, i) => (
                        <Row key={i} testid={`patron-prize-${i}`}
                             left={<span className="flex items-center gap-2">
                                 <span className="font-mono text-xs">{v.code}</span>
                                 <PrizeChip state={v.state} t={t} testId={`patron-prize-state-${i}`} />
                             </span>}
                             note={[v.title, v.label].filter(Boolean).join(' · ')}
                             right={when(v.at)} />
                    ))}
                </Section>

                <Section icon={Wifi} t={t} testid="patron-profile-remote"
                         title={t('patronCommand.profile.remote')} rows={p.remote.length}>
                    {p.remote.map((r, i) => (
                        <Row key={i} testid={`patron-remote-${i}`}
                             left={r.label || r.game_id}
                             note={r.emailed ? t('patronCommand.profile.emailed') : ''}
                             right={when(r.at)} />
                    ))}
                </Section>
            </div>

            <Card data-testid="patron-profile-consent">
                <CardHeader className="pb-2">
                    <CardTitle className="text-base">{t('patronCommand.profile.consent')}</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                    {p.consent.accepted ? (
                        <>
                            <p className="text-sm italic" data-testid="patron-consent-sentence">
                                “{p.consent.text}”
                            </p>
                            <p className="text-xs text-slate-500" data-testid="patron-consent-at">
                                {t('patronCommand.profile.consentAt', {
                                    when: when(p.consent.at),
                                    language: (p.consent.language || '').toUpperCase(),
                                })}
                            </p>
                        </>
                    ) : (
                        <p className="text-sm text-slate-500" data-testid="patron-consent-none">
                            {t('patronCommand.profile.noConsent')}
                        </p>
                    )}
                    {p.consent.unsubscribed_at && (
                        <p className="text-xs text-amber-600" data-testid="patron-consent-unsub">
                            {t('patronCommand.profile.unsubscribedAt', { when: when(p.consent.unsubscribed_at) })}
                        </p>
                    )}
                </CardContent>
            </Card>
        </div>
    );
};
